import { Grid } from "@mui/material";
import EAIcon from "components/common/EAIcon";
import useStyles from "../../../style/js-style/components/thirdparties/PanelOwlinStyle";
import i18next from "i18next";
import { useCallback, useContext, useEffect, useState } from "react";
import { Context } from "states/Store";

import { FormProvider, useForm, useWatch } from "react-hook-form";
import { deactivateOwlin, getOwlinMonitoring, updateOwlinMonitoring } from "services/edge/OwlinService";
import { ERROR } from "services/common/Constants";
import { isEmpty } from "lodash";
import EAButton from "components/common/EAButton";
import { PrivilegeEnum, RoleBasedAccessControlContext } from "services/common/RolesUtils";
import OwlinWidget from "./OwlinWidget";
import EADialog from "components/common/EADialog";
import clsx from "clsx";
import OwlinForm from "./OwlinForm";
import ProcessPendingBloc from "components/common/ProcessPendingBloc";

const PanelOwlin = ({ thirdPartyId }) => {
    const { classes } = useStyles();
    const [state, dispatch] = useContext(Context);
    const [owlinData, setOwlinData] = useState();
    const [options, setOptions] = useState([]);
    const [remainingTagsNb, setRemainingTagsNb] = useState(10);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isModifyDialogOpen, setIsModifyDialogOpen] = useState(false);
    const [isModifyConfirmDialogOpen, setIsModifyConfirmDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);

    const mapTagsToOptions = (tagList) => {
        return tagList.map(t => {
            if (typeof t === 'object') {
                return t;
            }
            return { value: t, label: t };
        })
    }

    const fetchOwlinMonitoring = useCallback(async () => {
        try {
            const owlin = await getOwlinMonitoring(
                state.account.id,
                thirdPartyId
            );
            const tagsList = isEmpty(owlin?.content?.tags) ? [] : owlin?.content?.tags.split(",");
            const list = mapTagsToOptions(tagsList);
            setOwlinData({ ...owlin.content, tags: list, status: owlin?.content?.activationStatus });
            if (!isEmpty(tagsList)) {
                setOptions(mapTagsToOptions(tagsList));
            }
        } catch (error) {
            console.error(error);
        }
    }, [state.account.id, thirdPartyId])

    useEffect(() => {
        if (state.account.id && thirdPartyId) {
            fetchOwlinMonitoring();
        }
    }, [state.account.id, thirdPartyId, fetchOwlinMonitoring]);


    const formMethods = useForm({
        mode: "onChange",
        delayError: 500,
        defaultValues: { "owlinUrl": "", "owlinTags": [] }
    });
    let listTags = useWatch({ control: formMethods.control, name: "owlinTags" });

    const {
        setValue
    } = formMethods;

    useEffect(() => {
        setValue("owlinUrl", owlinData?.websiteUrl);
        setValue("owlinTags", owlinData?.tags);
    }, [setValue, owlinData]);

    const callUpdateOwlinMonitoring = async (newStatus) => {
        const tags = formMethods.getValues("owlinTags");
        const tagsAsString = tags.map(t => typeof t === "string" ? t : t.value).join(",");
        const data = {
            url: formMethods.getValues("owlinUrl"),
            tags: tagsAsString,
            status: newStatus
        }
        setIsLoading(true);
        try {
            await updateOwlinMonitoring(
                state.account.id,
                thirdPartyId,
                data,
                null
            );
            setIsLoading(false);
            if (isModifyDialogOpen === true) {
                setIsModifyDialogOpen(false);
                setIsModifyConfirmDialogOpen(true);
            }
        } catch (error) {
            setIsLoading(false);
            const err = error?.message?.messages?.error[0];
            dispatch({
                type: "ALERT",
                alert: { type: ERROR, msg: err?.code || "INTERNAL_ERROR" },
            });
        }
    };

    useEffect(() => {
        if (owlinData?.status === 'PENDING') {
            const interval = setInterval(() => {
                fetchOwlinMonitoring();
            }, 10000);
            return () => clearInterval(interval);
        }
    }, [fetchOwlinMonitoring, owlinData]);

    useEffect(() => {
        if (listTags) {
            setRemainingTagsNb(10 - listTags?.length);
        }
    }, [listTags]);

    const goBackToEdit = () => {
        setOwlinData({ ...owlinData, status: "DRAFT" });
        callUpdateOwlinMonitoring("DRAFT");
    }

    const openDeleteDialog = () => {
        setIsDeleteDialogOpen(true);
    };

    const deactivateOwlinHandler = async () => {
        try {
            await deactivateOwlin(
                state.account.id,
                thirdPartyId,
                null
            );
            goBackToEdit();
            setIsDeleteDialogOpen(false);
        } catch (error) {
            const err = error?.message?.messages?.error[0];
            dispatch({
                type: "ALERT",
                alert: { type: ERROR, msg: err?.code || "INTERNAL_ERROR" },
            });
        }
    };

    const openModifyDialog = () => {
        setIsModifyDialogOpen(true);
    };

    return (
        <>
            <div className={classes.owlinContent}>
                {(owlinData?.status === 'DRAFT' || owlinData?.status === 'DEACTIVATED') && (
                    <>
                        <FormProvider {...formMethods}>
                            <form
                                onBlur={formMethods.handleSubmit(() => callUpdateOwlinMonitoring(null))}
                                className={classes.owlinActivationForm}
                            >
                                <OwlinForm thirdPartyId={thirdPartyId}
                                    remainingTagsNb={remainingTagsNb}
                                    owlinData={owlinData}
                                    initialOptions={options}
                                    mapTagsToOptions={mapTagsToOptions}
                                    setData={setOwlinData}
                                    setHasError={setHasError} />
                            </form>
                        </FormProvider>
                    </>
                )}
                {owlinData?.status === 'PENDING' && (
                    <ProcessPendingBloc />
                )}
                {(owlinData?.status === 'ACTIVATED')  && (
                    <Grid container>
                        <Grid item xs={10}>
                            <OwlinWidget owlinData={owlinData} thirdPartyId={thirdPartyId}></OwlinWidget>
                        </Grid>
                        <Grid item xs={2} className={classes.buttonWrapper}>
                            <EAButton
                                colorVariant="primary"
                                onClick={openModifyDialog}
                                customStyle={{ boxShadow: "none", minHeight: "30px" }}
                                disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_OWLIN_SOURCE)}
                                className={classes.buttonSmall}
                                content={
                                    <span className={classes.buttonModifyContent}>
                                        <EAIcon icon="edit_white" />
                                        {i18next.t("owlin.activated.updateButton")}
                                    </span>}
                            />
                            <EAButton
                                colorVariant="errorLight"
                                onClick={openDeleteDialog}
                                customStyle={{ boxShadow: "none", minHeight: "30px" }}
                                disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_OWLIN_SOURCE)}
                                className={classes.buttonSmall}
                                content={
                                    <span className={classes.buttonStopContent}>
                                        <EAIcon icon="stop_scoring" />
                                        {i18next.t("owlin.activated.deactivationButton")}
                                    </span>}
                            />
                        </Grid>
                    </Grid>
                )}
                {owlinData?.status === 'FAILED' && (
                    <>
                        <span onClick={goBackToEdit} className={classes.failedBackButton}><EAIcon icon={"keyboard_backspace"} /> <span className={classes.failedSubTitle} >Retour</span></span>
                        <div className={classes.failedIcon}><EAIcon icon={"dangerous_grey_light"} /></div>
                        <div className={classes.failedTitle} >{i18next.t("owlin.failed")}</div>
                    </>
                )}
            </div>

            {/* PopUp : on delete */}
            {isDeleteDialogOpen && (
                <EADialog
                    isOpen={isDeleteDialogOpen}
                    onCancel={() => setIsDeleteDialogOpen(false)}
                    onClose={() => setIsDeleteDialogOpen(false)}
                    onValidate={() => deactivateOwlinHandler()}
                    variant="redValid"
                    btnSize="large"
                    maxWidth="md">

                    <div className={classes.root}>
                        <div className={clsx(classes.imgWrapper, classes.imgWrapperRed)}>
                            <EAIcon icon={"stop_scoring"} className={classes.imgContent} />
                        </div>
                        <span className={classes.bolder}>
                            {i18next.t("owlin.activated.deactivationTitle")}
                        </span>
                        <span className={classes.caption}>
                            {i18next.t("owlin.activated.deactivationCaption")}
                        </span>
                    </div>

                </EADialog>
            )}

            {/* PopUp : on modify */}
            {isModifyDialogOpen && (
                <EADialog
                    isOpen={isModifyDialogOpen}
                    onClose={() => setIsModifyDialogOpen(false)}
                    hasActions={false}
                    maxWidth="md">

                    <div className={classes.root}>
                        <span className={classes.leftAlignedTitle}>
                            {i18next.t("owlin.activated.updateTitle")}
                        </span>
                        <FormProvider {...formMethods}>
                            <form
                                className={classes.owlinActivationForm}
                            >
                                <OwlinForm thirdPartyId={thirdPartyId}
                                    remainingTagsNb={remainingTagsNb}
                                    initialOptions={options}
                                    isDialogMode={true}
                                    owlinData={owlinData}
                                    mapTagsToOptions={mapTagsToOptions}
                                    setData={setOwlinData}
                                    setHasError={setHasError} />
                            </form>
                        </FormProvider>

                    </div>
                    <div className={classes.formContainer} style={{ justifyContent: "flex-end" }}>
                        {/* Cancel */}
                        <EAButton
                            onClick={() => setIsModifyDialogOpen(false)}
                            content={i18next.t("cancel")}
                            size={"regular"}
                            colorVariant={"secondary"}
                        />

                        {/* Confirm */}
                        <EAButton
                            onClick={() => callUpdateOwlinMonitoring()}
                            content={i18next.t("validate")}
                            size={"regular"}
                            colorVariant={"primary"}
                            disabled={hasError}
                            className={classes.buttonWithMargin}
                            type="submit"
                            validateLoading={isLoading}
                        />
                        
                    </div>

                </EADialog>
            )}

            {/* PopUp : modify confirm */}
            {isModifyConfirmDialogOpen && (
                <EADialog
                    isOpen={isModifyConfirmDialogOpen}
                    onClose={() => setIsModifyConfirmDialogOpen(false)}
                    hasActions={false}
                    title={i18next.t("owlin.activated.updateConfirmationTitle")}
                    btnSize="large"
                    maxWidth="md">

                    <div className={classes.root}>
                        <div className={classes.popupConfirmContainer}>
                            <span className={clsx(classes.imgWrapper, classes.imgWrapperGreen)}>
                                <EAIcon icon={"edit_green_bis"} className={classes.imgContent} />
                            </span>
                            <span className={classes.confirmCaption}>
                                {i18next.t("owlin.activated.updateConfirmationCaption")}
                            </span>
                        </div>
                    </div>
                </EADialog>
            )}
        </>
    )

}
export default PanelOwlin;
